import React, { useState, useEffect } from 'react';
import { X, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Location } from '@/types';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';

interface LocationFormProps {
  location?: Location | null;
  onClose: () => void;
  onSubmit: () => void;
  locations: Location[];
}

const LocationForm: React.FC<LocationFormProps> = ({
  location,
  onClose,
  onSubmit,
  locations,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentId: '',
    plantNumber: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name,
        description: location.description || '',
        parentId: location.parentId || '',
        plantNumber: location.plantNumber || '',
      });
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    setLoading(true);
    try {
      const data = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        parentId: formData.parentId || undefined,
        plantNumber: formData.plantNumber.trim() || undefined,
      };

      if (location) {
        await apiService.put(`/locations/${location.id}`, data);
        toast.success('Standort erfolgreich aktualisiert');
      } else {
        await apiService.post('/locations', data);
        toast.success('Standort erfolgreich erstellt');
      }

      onSubmit();
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error('Fehler beim Speichern des Standorts');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Filtere verfügbare Eltern-Standorte (verhindere zirkuläre Referenzen)
  const availableParents = locations.filter(loc => {
    if (!location) return true; // Beim Erstellen sind alle Standorte verfügbar
    if (loc.id === location.id) return false; // Kann nicht sein eigener Vater sein
    if (loc.parentId === location.id) return false; // Seine Kinder können nicht sein Vater sein
    return true;
  });

  // Erstelle hierarchische Anzeige für Standorte
  const getLocationPath = (locationId: string): string[] => {
    const path: string[] = [];
    let currentId: string | undefined = locationId;
    
    while (currentId) {
      const location = locations.find(loc => loc.id === currentId);
      if (!location) break;
      path.unshift(location.name);
      currentId = location.parentId;
    }
    
    return path;
  };

  const getLocationDisplayName = (loc: Location): string => {
    const path = getLocationPath(loc.id);
    const indent = '  '.repeat(path.length - 1);
    
    if (path.length === 1) {
      return `${indent}📍 ${loc.name}`;
    } else {
      const parentPath = path.slice(0, -1).join(' > ');
      return `${indent}📍 ${loc.name} (${parentPath})`;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {location ? 'Standort bearbeiten' : 'Neuer Standort'}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="z.B. Hauptgebäude, Lager A, Raum 101"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optionale Beschreibung des Standorts"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="plantNumber">Werknummer</Label>
              <Input
                id="plantNumber"
                value={formData.plantNumber}
                onChange={(e) => handleInputChange('plantNumber', e.target.value)}
                placeholder="z.B. 1000, 2000, 3000"
              />
              <p className="text-xs text-muted-foreground">
                Werknummer für SAP-Integration. Mehrere Standorte können sich eine Werknummer teilen.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="parentId">Übergeordneter Standort</Label>
              <select
                id="parentId"
                value={formData.parentId}
                onChange={(e) => handleInputChange('parentId', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
              >
                <option value="">Kein übergeordneter Standort (Hauptstandort)</option>
                {availableParents
                  .sort((a, b) => {
                    // Sortiere nach Hierarchie-Level (Hauptstandorte zuerst), dann alphabetisch
                    const aPath = getLocationPath(a.id);
                    const bPath = getLocationPath(b.id);
                    
                    if (aPath.length !== bPath.length) {
                      return aPath.length - bPath.length;
                    }
                    
                    return a.name.localeCompare(b.name);
                  })
                  .map((parent) => (
                    <option key={parent.id} value={parent.id}>
                      {getLocationDisplayName(parent)}
                    </option>
                  ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Wählen Sie einen übergeordneten Standort für einen Unterstandort. Die Hierarchie wird in Klammern angezeigt.
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Speichern...' : (location ? 'Aktualisieren' : 'Erstellen')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LocationForm;
